import React, { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import { 
  Activity, AlertTriangle, CheckCircle, Target, Users, TrendingUp, 
  Lightbulb, Eye, Heart, Building, TreePine, Wallet, ArrowRight,
  RefreshCw, Download, ChevronRight
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Velocímetro circular para el Health Score
const HealthGauge = ({ score, statusLabel, statusColor }) => {
  const circumference = 2 * Math.PI * 45;
  const strokeDashoffset = circumference - (score / 100) * circumference;
  
  return (
    <div className="relative w-40 h-40 mx-auto" data-testid="health-gauge">
      <svg className="w-full h-full transform -rotate-90">
        {/* Background circle */}
        <circle
          cx="80"
          cy="80"
          r="45"
          stroke="#e5e7eb"
          strokeWidth="10"
          fill="none"
        />
        {/* Progress circle */}
        <circle
          cx="80"
          cy="80"
          r="45"
          stroke={statusColor}
          strokeWidth="10"
          fill="none"
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={strokeDashoffset}
          className="transition-all duration-1000 ease-out"
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className="text-3xl font-bold text-[#384451]">{score}%</span>
        <span className="text-sm text-[#384451]/70">{statusLabel}</span>
      </div>
    </div>
  );
};

// Componente de breakdown por categoría
const CategoryBreakdown = ({ breakdown, categoryFill }) => {
  const categories = [
    { key: "completeness", label: "Completitud", icon: CheckCircle, color: "bg-blue-500" },
    { key: "market_alignment", label: "Alineación Mercado", icon: Target, color: "bg-green-500" },
    { key: "demand_match", label: "Demanda Match", icon: TrendingUp, color: "bg-purple-500" }
  ];

  return (
    <div className="space-y-4" data-testid="category-breakdown">
      {categories.map(({ key, label, icon: Icon, color }) => (
        <div key={key} className="space-y-1">
          <div className="flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Icon className="w-4 h-4 text-[#384451]/60" />
              <span className="text-[#384451]">{label}</span>
            </div>
            <span className="font-medium text-[#384451]">{breakdown[key]}%</span>
          </div>
          <Progress value={breakdown[key]} className="h-2" />
        </div>
      ))}
      
      {categoryFill && (
        <div className="mt-6 pt-4 border-t">
          <h4 className="text-sm font-medium text-[#384451] mb-3">Llenado por Categoría</h4>
          <div className="grid grid-cols-2 gap-3">
            {Object.entries(categoryFill).map(([cat, value]) => {
              const catLabels = {
                emotional: { label: "Emocional", icon: Heart, color: "text-rose-500" },
                technical: { label: "Técnico", icon: Building, color: "text-blue-500" },
                urban: { label: "Urbano", icon: TreePine, color: "text-green-500" },
                financial: { label: "Financiero", icon: Wallet, color: "text-amber-500" }
              };
              const config = catLabels[cat];
              const Icon = config.icon;
              
              return (
                <div key={cat} className="flex items-center gap-2 text-sm">
                  <Icon className={`w-4 h-4 ${config.color}`} />
                  <span className="text-[#384451]/70">{config.label}:</span>
                  <span className="font-medium text-[#384451]">{value}%</span>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

// Componente de Gap Analysis
const GapAnalysis = ({ gapData }) => {
  const [activeTab, setActiveTab] = useState("strengths");
  
  const tabs = [
    { id: "strengths", label: "Fortalezas", icon: CheckCircle, color: "text-green-500", data: gapData.strengths },
    { id: "blind_spots", label: "Puntos Ciegos", icon: AlertTriangle, color: "text-red-500", data: gapData.blind_spots },
    { id: "opportunities", label: "Oportunidades", icon: Lightbulb, color: "text-amber-500", data: gapData.opportunities }
  ];

  return (
    <div className="space-y-4" data-testid="gap-analysis">
      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        {tabs.map(({ id, label, icon: Icon, color, data }) => (
          <button
            key={id}
            onClick={() => setActiveTab(id)}
            className={`p-3 rounded-lg border transition-all ${
              activeTab === id ? 'border-primary bg-primary/5' : 'border-gray-200 hover:border-gray-300'
            }`}
          >
            <Icon className={`w-5 h-5 mx-auto mb-1 ${color}`} />
            <p className="text-2xl font-bold text-[#384451]">{data?.length || 0}</p>
            <p className="text-xs text-[#384451]/60">{label}</p>
          </button>
        ))}
      </div>
      
      {/* Detail list */}
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {tabs.find(t => t.id === activeTab)?.data?.map((item, idx) => (
          <div 
            key={idx} 
            className="p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-[#384451]">{item.name}</span>
                  <Badge variant="outline" className="text-xs">
                    {item.market_demand}% demanda
                  </Badge>
                </div>
                {item.tip && (
                  <p className="text-xs text-[#384451]/60 mt-1">{item.tip}</p>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      
      {/* Potential improvement */}
      {gapData.summary?.potential_improvement > 0 && (
        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
          <p className="text-sm text-green-700">
            <TrendingUp className="w-4 h-4 inline mr-1" />
            Potencial de mejora: <strong>+{gapData.summary.potential_improvement.toFixed(1)}%</strong> al completar puntos ciegos
          </p>
        </div>
      )}
    </div>
  );
};

// Componente de Matriz de Audiencias
const AudienceMatrix = ({ audienceData }) => {
  const profiles = audienceData.all_profiles || {};
  
  return (
    <div className="space-y-4" data-testid="audience-matrix">
      {/* Current Audience */}
      {audienceData.current_audience?.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-[#384451] mb-2 flex items-center gap-2">
            <Users className="w-4 h-4 text-green-500" />
            Audiencia Actual
          </h4>
          <div className="space-y-2">
            {audienceData.current_audience.map((aud, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-green-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{aud.profile?.icon}</span>
                  <div>
                    <p className="font-medium text-[#384451]">{aud.profile?.name}</p>
                    <p className="text-xs text-[#384451]/60">{aud.actual_matches} matches</p>
                  </div>
                </div>
                <Badge className="bg-green-500">{aud.compatibility}%</Badge>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* Missed Opportunities */}
      {audienceData.missed_opportunities?.length > 0 && (
        <div>
          <h4 className="text-sm font-medium text-[#384451] mb-2 flex items-center gap-2">
            <AlertTriangle className="w-4 h-4 text-amber-500" />
            Oportunidades Perdidas
          </h4>
          <div className="space-y-2">
            {audienceData.missed_opportunities.map((aud, idx) => (
              <div key={idx} className="flex items-center justify-between p-2 bg-amber-50 rounded-lg">
                <div className="flex items-center gap-2">
                  <span className="text-2xl">{aud.profile?.icon}</span>
                  <div>
                    <p className="font-medium text-[#384451]">{aud.profile?.name}</p>
                    <p className="text-xs text-[#384451]/60">{aud.profile?.description}</p>
                  </div>
                </div>
                <Badge variant="outline" className="border-amber-500 text-amber-700">
                  {aud.compatibility}% compatible
                </Badge>
              </div>
            ))}
          </div>
        </div>
      )}
      
      {/* All profiles grid */}
      <div>
        <h4 className="text-sm font-medium text-[#384451] mb-2">Todos los Perfiles</h4>
        <div className="grid grid-cols-2 gap-2">
          {Object.entries(profiles).slice(0, 6).map(([id, data]) => (
            <div key={id} className="p-2 border rounded-lg text-center">
              <span className="text-xl">{data.profile?.icon}</span>
              <p className="text-xs font-medium text-[#384451] truncate">{data.profile?.name}</p>
              <p className="text-lg font-bold text-[#384451]">{data.compatibility}%</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Componente de Embudo Predictivo
const PredictiveFunnel = ({ funnelData }) => {
  const stages = [
    { key: "reach", label: "Alcance", icon: Eye },
    { key: "engaged", label: "Engagement", icon: Activity },
    { key: "interested", label: "Interesados", icon: Heart },
    { key: "matched", label: "Matches", icon: Target },
    { key: "contacted", label: "Contactados", icon: Users }
  ];
  
  const maxValue = Math.max(...Object.values(funnelData.funnel || {}), 1);
  
  return (
    <div className="space-y-4" data-testid="predictive-funnel">
      {/* Funnel visualization */}
      <div className="space-y-2">
        {stages.map(({ key, label, icon: Icon }, idx) => {
          const value = funnelData.funnel?.[key] || 0;
          const width = (value / maxValue) * 100;
          
          return (
            <div key={key} className="flex items-center gap-3">
              <div className="w-24 flex items-center gap-2 text-sm text-[#384451]/70">
                <Icon className="w-4 h-4" />
                {label}
              </div>
              <div className="flex-1 relative">
                <div 
                  className="h-8 bg-primary/80 rounded-r-full flex items-center justify-end pr-3 transition-all duration-500"
                  style={{ width: `${Math.max(width, 10)}%` }}
                >
                  <span className="text-sm font-bold text-[#384451]">{value}</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
      
      {/* Conversion rates */}
      <div className="grid grid-cols-3 gap-3 pt-4 border-t">
        <div className="text-center p-2 bg-gray-50 rounded-lg">
          <p className="text-2xl font-bold text-[#384451]">
            {funnelData.conversion_rates?.view_to_swipe || 0}%
          </p>
          <p className="text-xs text-[#384451]/60">Vista → Swipe</p>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded-lg">
          <p className="text-2xl font-bold text-[#384451]">
            {funnelData.conversion_rates?.swipe_to_like || 0}%
          </p>
          <p className="text-xs text-[#384451]/60">Swipe → Like</p>
        </div>
        <div className="text-center p-2 bg-gray-50 rounded-lg">
          <p className="text-2xl font-bold text-[#384451]">
            {funnelData.conversion_rates?.like_to_match || 0}%
          </p>
          <p className="text-xs text-[#384451]/60">Like → Match</p>
        </div>
      </div>
      
      {/* Average Affinity */}
      <div className="p-3 bg-primary/10 rounded-lg">
        <p className="text-sm text-[#384451]">
          <strong>Afinidad Promedio:</strong> {funnelData.average_affinity || 0}%
        </p>
      </div>
      
      {/* Abandonment Analysis */}
      {funnelData.abandonment_analysis?.primary_drop_reason && (
        <div className="p-3 bg-red-50 rounded-lg border border-red-200">
          <p className="text-sm text-red-700">
            <AlertTriangle className="w-4 h-4 inline mr-1" />
            Principal razón de abandono: <strong className="capitalize">
              {funnelData.abandonment_analysis.primary_drop_reason}
            </strong>
          </p>
        </div>
      )}
    </div>
  );
};

// Componente de Recomendaciones
const Recommendations = ({ recommendations, onActionClick }) => {
  const priorityColors = {
    high: "bg-red-100 border-red-300 text-red-800",
    medium: "bg-amber-100 border-amber-300 text-amber-800",
    low: "bg-green-100 border-green-300 text-green-800"
  };
  
  return (
    <div className="space-y-3" data-testid="recommendations">
      {recommendations.map((rec, idx) => (
        <div 
          key={idx} 
          className={`p-4 rounded-lg border ${priorityColors[rec.priority] || 'bg-gray-100 border-gray-300'}`}
        >
          <div className="flex items-start gap-3">
            <span className="text-2xl">{rec.icon}</span>
            <div className="flex-1">
              <div className="flex items-center gap-2 mb-1">
                <h4 className="font-medium">{rec.title}</h4>
                <Badge variant="outline" className="text-xs uppercase">
                  {rec.priority}
                </Badge>
              </div>
              <p className="text-sm opacity-80 mb-2">{rec.description}</p>
              <div className="flex items-center justify-between">
                <span className="text-xs font-medium text-green-700">{rec.impact}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => onActionClick && onActionClick(rec)}
                  data-testid={`ric-rec-action-${idx}`}
                >
                  {rec.action} <ChevronRight className="w-3 h-3 ml-1" />
                </Button>
              </div>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

// Componente principal del RIC Report
export const RICReport = ({ propertyId, onClose, onEditProperty }) => {
  const [loading, setLoading] = useState(true);
  const [ricData, setRicData] = useState(null);
  const [activeTab, setActiveTab] = useState("health");

  useEffect(() => {
    if (propertyId) {
      fetchRICReport();
    }
  }, [propertyId]);

  const fetchRICReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/ric/${propertyId}`);
      setRicData(response.data);
    } catch (error) {
      toast.error("Error al cargar el reporte de inteligencia");
      console.error("RIC Error:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Card className="w-full">
        <CardContent className="p-8 text-center">
          <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-[#384451]/70">Generando Reporte de Inteligencia Comercial...</p>
        </CardContent>
      </Card>
    );
  }

  if (!ricData) {
    return (
      <Card className="w-full">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 text-amber-500 mx-auto mb-4" />
          <p className="text-[#384451]">No se pudo cargar el reporte</p>
          <Button onClick={fetchRICReport} className="mt-4">
            <RefreshCw className="w-4 h-4 mr-2" />
            Reintentar
          </Button>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full" data-testid="ric-report">
      <CardHeader className="pb-4">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-xl text-[#384451] flex items-center gap-2">
              <Activity className="w-5 h-5 text-primary" />
              Reporte de Inteligencia Comercial
            </CardTitle>
            <CardDescription>
              {ricData.property_title} • Generado: {new Date(ricData.generated_at).toLocaleDateString('es-MX')}
            </CardDescription>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={fetchRICReport}>
              <RefreshCw className="w-4 h-4" />
            </Button>
            {onClose && (
              <Button variant="ghost" size="sm" onClick={onClose}>
                ✕
              </Button>
            )}
          </div>
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-5 mb-4">
            <TabsTrigger value="health" className="text-xs">Salud</TabsTrigger>
            <TabsTrigger value="gap" className="text-xs">Gap Analysis</TabsTrigger>
            <TabsTrigger value="audience" className="text-xs">Audiencias</TabsTrigger>
            <TabsTrigger value="funnel" className="text-xs">Embudo</TabsTrigger>
            <TabsTrigger value="tips" className="text-xs">Tips</TabsTrigger>
          </TabsList>
          
          <TabsContent value="health" className="space-y-4">
            <div className="grid md:grid-cols-2 gap-6">
              <div className="text-center">
                <h3 className="text-sm font-medium text-[#384451] mb-4">Score de Salud de la Propiedad</h3>
                <HealthGauge 
                  score={ricData.health_score.score}
                  statusLabel={ricData.health_score.status_label}
                  statusColor={ricData.health_score.status_color}
                />
              </div>
              <div>
                <h3 className="text-sm font-medium text-[#384451] mb-4">Desglose del Score</h3>
                <CategoryBreakdown 
                  breakdown={ricData.health_score.breakdown}
                  categoryFill={ricData.health_score.category_fill}
                />
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="gap">
            <GapAnalysis gapData={ricData.gap_analysis} />
          </TabsContent>
          
          <TabsContent value="audience">
            <AudienceMatrix audienceData={ricData.audience_matrix} />
          </TabsContent>
          
          <TabsContent value="funnel">
            <PredictiveFunnel funnelData={ricData.predictive_funnel} />
          </TabsContent>
          
          <TabsContent value="tips">
            <Recommendations
              recommendations={ricData.recommendations}
              onActionClick={(rec) => {
                if (!onEditProperty) return;
                // Route action to the right edit tab based on recommendation text
                const haystack = `${rec.action || ""} ${rec.title || ""} ${rec.description || ""}`.toLowerCase();
                let tab = "info";
                if (haystack.includes("foto") || haystack.includes("imagen")) tab = "photos";
                else if (haystack.includes("ficha") || haystack.includes("perfil") || haystack.includes("psicograf") || haystack.includes("ola") || haystack.includes("factor") || haystack.includes("vibe") || haystack.includes("complet")) tab = "psychographic";
                onEditProperty(propertyId, tab);
              }}
            />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default RICReport;
